import { useState } from 'react';
import type { Story, StoryActivity } from '@/contracts/content';
import { Scene } from '@/content/scenes';
import { Button } from '@/design-system/Button';
import { cn } from '@/design-system/cn';
import { SparkleGlyph } from '@/design-system/Sparkle';
import { IconArrow, IconBack, IconSpeaker } from '@/features/studio/Icons';

export type ParticipationMethod = 'answered' | 'helped' | 'accommodation';

interface Props {
  story: Story;
  onComplete(method: ParticipationMethod): void;
  onExit(): void;
  onRevisitPage(pageId: string): void;
  /** Bedtime offers finishing another day rather than starting a picture now. */
  bedtime?: boolean;
  onFinishTomorrow?(): void;
}

/**
 * Explore the story (spec §7 C05).
 *
 * There is no score, no pass mark and no failure screen. A wrong answer gets a
 * neutral hint and another try; "Help me" gives a guided completion; an
 * accessibility skip is available. How the child got there is recorded
 * honestly, and creative access follows PARTICIPATION, not correctness.
 */
export function StoryActivities({
  story, onComplete, onExit, onRevisitPage, bedtime, onFinishTomorrow,
}: Props) {
  const activities = story.currentVersion.activities;
  const [step, setStep] = useState(0);
  const [method, setMethod] = useState<ParticipationMethod>('answered');
  const activity = activities[step];
  const isLast = step === activities.length - 1;

  const speak = (text: string) => {
    if (!('speechSynthesis' in window)) return;
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.rate = 0.85;
    window.speechSynthesis.speak(u);
  };

  const advance = (usedHelp: boolean) => {
    if (usedHelp && method === 'answered') setMethod('helped');
    if (isLast) onComplete(usedHelp && method === 'answered' ? 'helped' : method);
    else setStep((s) => s + 1);
  };

  return (
    <div className="flex min-h-[100dvh] flex-col bg-paper">
      <header className="flex shrink-0 items-center gap-m px-l py-m">
        <button
          onClick={onExit}
          className="flex min-h-touch items-center gap-xs rounded-pebble border-2 border-line bg-card px-m font-display text-[15px] font-semibold text-ink-soft"
        >
          <IconBack aria-hidden />
          <span className="max-[420px]:sr-only">Back</span>
        </button>
        <p className="flex-1 text-center font-display text-[14px] font-semibold text-ink-faint">
          Exploring — {step + 1} of {activities.length}
        </p>
        <button
          onClick={() => {
            setMethod('accommodation');
            onComplete('accommodation');
          }}
          className="min-h-touch rounded-pebble px-m font-display text-[14px] font-semibold text-ink-faint underline underline-offset-4"
        >
          Skip to making
        </button>
      </header>

      <main className="mx-auto w-full max-w-3xl flex-1 px-l pb-2xl">
        <ActivityCard
          key={activity.id}
          activity={activity}
          onSpeak={speak}
          onRevisitPage={onRevisitPage}
          onDone={advance}
          lastOne={isLast}
          bedtime={bedtime}
          onFinishTomorrow={onFinishTomorrow}
        />
      </main>
    </div>
  );
}

function ActivityCard({
  activity,
  onSpeak,
  onRevisitPage,
  onDone,
  lastOne,
  bedtime,
  onFinishTomorrow,
}: {
  activity: StoryActivity;
  onSpeak(text: string): void;
  onRevisitPage(pageId: string): void;
  onDone(usedHelp: boolean): void;
  lastOne: boolean;
  bedtime?: boolean;
  onFinishTomorrow?(): void;
}) {
  const [picked, setPicked] = useState<string | null>(null);
  const [showHint, setShowHint] = useState(false);
  const [helped, setHelped] = useState(false);
  const [order, setOrder] = useState<string[]>([]);
  const [settled, setSettled] = useState(false);

  const prompt = (
    <div className="mb-xl flex items-start gap-m">
      <button
        onClick={() => onSpeak(activity.prompt)}
        aria-label="Read the question aloud"
        className="mt-[3px] flex h-touch w-touch shrink-0 items-center justify-center rounded-full border-2 border-line bg-card text-primary"
      >
        <IconSpeaker aria-hidden />
      </button>
      <h1 className="font-display text-[24px] font-semibold leading-snug text-ink sm:text-[30px]">
        {activity.prompt}
      </h1>
    </div>
  );

  /* ---------------- Picture choice ---------------- */
  if (activity.kind === 'picture-choice') {
    const correct = activity.correctOptionId;
    const isRight = picked !== null && picked === correct;

    return (
      <section className="animate-slide-up">
        {prompt}
        <div className="grid gap-l sm:grid-cols-3">
          {activity.options?.map((o) => {
            const chosen = picked === o.id;
            const reveal = settled && o.id === correct;
            return (
              <button
                key={o.id}
                onClick={() => {
                  if (settled) return;
                  setPicked(o.id);
                  if (o.id === correct) setSettled(true);
                  else setShowHint(true);
                }}
                className={cn(
                  'overflow-hidden rounded-blob border-[3px] bg-card text-left shadow-cut transition',
                  'focus-visible:outline-none',
                  reveal || (chosen && isRight)
                    ? 'border-leaf ring-4 ring-leaf/25'
                    : chosen
                      ? 'border-secondary'
                      : 'border-line hover:-translate-y-1',
                )}
              >
                <Scene id={o.sceneId} className="aspect-[4/3] w-full" />
                <span className="flex min-h-touch-lg items-center justify-center px-m font-display text-[17px] font-semibold text-ink">
                  {o.label}
                </span>
              </button>
            );
          })}
        </div>

        {showHint && !settled ? (
          <div className="mt-l flex flex-wrap items-center gap-m rounded-pebble border-2 border-dashed border-line bg-sunny/15 p-l">
            <p className="flex-1 text-[16px] text-ink-soft">{activity.hint}</p>
            {activity.hintPageId ? (
              <Button variant="outline" size="sm" onClick={() => onRevisitPage(activity.hintPageId!)}>
                Look again
              </Button>
            ) : null}
            <Button
              variant="soft"
              size="sm"
              onClick={() => {
                setHelped(true);
                setSettled(true);
                setPicked(correct ?? null);
              }}
            >
              Help me
            </Button>
          </div>
        ) : null}

        {settled ? (
          <Footer helped={helped} lastOne={lastOne} onDone={() => onDone(helped)} bedtime={bedtime} onFinishTomorrow={onFinishTomorrow} />
        ) : null}
      </section>
    );
  }

  /* ---------------- Sequence (tap to place, never drag-only) ---------------- */
  if (activity.kind === 'sequence') {
    const remaining = activity.options?.filter((o) => !order.includes(o.id)) ?? [];
    const done = remaining.length === 0;
    const correctOrder = activity.correctOrder ?? [];
    const isRight = done && order.every((id, i) => id === correctOrder[i]);

    return (
      <section className="animate-slide-up">
        {prompt}
        {/* Tap-to-place alternative to dragging (spec §7 C05, §18.2). */}
        <ol className="mb-xl grid gap-m sm:grid-cols-3">
          {[0, 1, 2].map((slot) => {
            const id = order[slot];
            const opt = activity.options?.find((o) => o.id === id);
            return (
              <li
                key={slot}
                className={cn(
                  'flex min-h-[128px] flex-col items-center justify-center rounded-blob border-[3px] border-dashed p-m text-center',
                  opt ? 'border-primary bg-card' : 'border-line bg-paper-deep/50',
                )}
              >
                <span className="mb-xs font-display text-[13px] font-semibold uppercase tracking-wide text-ink-faint">
                  {['First', 'Next', 'Last'][slot]}
                </span>
                {opt ? (
                  <button
                    onClick={() => setOrder((o) => o.filter((x) => x !== opt.id))}
                    className="w-full"
                  >
                    <Scene id={opt.sceneId} className="h-16 w-full rounded-pebble" />
                    <span className="mt-xs block font-display text-[14px] font-semibold">{opt.label}</span>
                  </button>
                ) : (
                  <span className="text-[14px] text-ink-faint">Tap a picture below</span>
                )}
              </li>
            );
          })}
        </ol>

        <div className="grid gap-m sm:grid-cols-3">
          {remaining.map((o) => (
            <button
              key={o.id}
              onClick={() => setOrder((prev) => [...prev, o.id])}
              className="overflow-hidden rounded-blob border-[3px] border-line bg-card shadow-cut transition hover:-translate-y-1"
            >
              <Scene id={o.sceneId} className="aspect-[4/3] w-full" />
              <span className="flex min-h-touch items-center justify-center px-m font-display text-[16px] font-semibold">
                {o.label}
              </span>
            </button>
          ))}
        </div>

        {done && !isRight ? (
          <div className="mt-l flex flex-wrap items-center gap-m rounded-pebble border-2 border-dashed border-line bg-sunny/15 p-l">
            <p className="flex-1 text-[16px] text-ink-soft">{activity.hint}</p>
            <Button variant="outline" size="sm" onClick={() => setOrder([])}>
              Try again
            </Button>
            <Button
              variant="soft"
              size="sm"
              onClick={() => {
                setHelped(true);
                setOrder(correctOrder);
              }}
            >
              Help me
            </Button>
          </div>
        ) : null}

        {done && isRight ? <Footer helped={helped} lastOne={lastOne} onDone={() => onDone(helped)} bedtime={bedtime} onFinishTomorrow={onFinishTomorrow} /> : null}
      </section>
    );
  }

  /* ---------------- Reflection — no right answer, nothing recorded ---------------- */
  return (
    <section className="animate-slide-up">
      {prompt}
      <div className="u-card bg-accent-soft/60 p-xl">
        <p className="text-[17px] leading-relaxed text-ink-soft">
          There is no right answer to this one. Say it out loud to someone with you, or just
          think about it — then go and make your picture.
        </p>
        <p className="mt-m text-[14px] text-ink-faint">
          Nothing you say here is recorded or saved.
        </p>
      </div>
      <Footer
        helped={false}
        lastOne={lastOne}
        onDone={() => onDone(false)}
        bedtime={bedtime}
        onFinishTomorrow={onFinishTomorrow}
      />
    </section>
  );
}

function Footer({
  helped, lastOne, onDone, bedtime, onFinishTomorrow,
}: {
  helped: boolean;
  lastOne: boolean;
  onDone(): void;
  bedtime?: boolean;
  onFinishTomorrow?(): void;
}) {
  return (
    <div className="mt-2xl flex flex-col items-center gap-m rounded-blob border-2 border-line bg-card p-xl text-center shadow-cut animate-pop-in">
      <SparkleGlyph className="h-8 w-8 text-magic" />
      <p className="font-display text-[20px] font-semibold text-ink">
        {helped ? 'Nice thinking — we got there together.' : 'You explored the story.'}
      </p>
      <Button variant="magic" size="lg" onClick={onDone} iconRight={<IconArrow />}>
        {lastOne ? 'Make your picture' : 'Next'}
      </Button>
      {/* Bedtime: stopping here is a real, offered choice, not a failure to
          continue (spec §8.4, §7 C04). */}
      {lastOne && bedtime && onFinishTomorrow ? (
        <button
          onClick={onFinishTomorrow}
          className="min-h-touch rounded-pebble px-l font-display text-[16px] font-semibold text-ink-soft underline underline-offset-4"
        >
          Finish making tomorrow
        </button>
      ) : null}
    </div>
  );
}
